import { Link } from "react-router-dom";
import { Home } from "lucide-react";
import { CALCULATOR_LIST } from "@/lib/calculators";
import { RESEARCH_CLUSTERS, STATE_OVERVIEW_LINK, FREE_TOOLS, PILLAR_GUIDES } from "@/lib/research-clusters";
import { ANGI_HOMEPAGE_LINK } from "@/lib/angi-affiliate-links";
import { track } from "@/lib/analytics";

const calcLinks = CALCULATOR_LIST.map((c) => ({ to: c.path, label: c.name }));

export default function Footer() {
  return (
    <footer className="border-t border-slate-200 bg-slate-50" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-10 md:grid-cols-3 lg:grid-cols-6">
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900 text-white">
                <Home className="h-5 w-5" />
              </span>
              <span className="font-display text-lg font-semibold text-slate-900">
                HavenCostGuide
              </span>
            </Link>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-600">
              Free, state-specific home renovation cost estimates built from publicly
              available contractor data and industry benchmarks. Updated twice yearly.
            </p>
            <p className="mt-3 text-sm text-slate-600">
              Questions?{" "}
              <a
                href="mailto:contact@havencostguide.com"
                className="font-medium text-slate-900 hover:text-green-700"
                data-testid="footer-contact-email"
              >
                contact@havencostguide.com
              </a>
            </p>
          </div>

          <div>
            <h4 className="font-display text-sm font-semibold text-slate-900">Calculators</h4>
            <ul className="mt-3 space-y-2">
              {calcLinks.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-sm text-slate-600 hover:text-slate-900"
                    data-testid={`footer-link-${l.to.replace(/\//g, "")}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display text-sm font-semibold text-slate-900">Research tools</h4>
            <ul className="mt-3 space-y-2" data-testid="footer-research-tools">
              <li>
                <Link
                  to={STATE_OVERVIEW_LINK.path}
                  className="text-sm font-medium text-slate-700 hover:text-slate-900"
                  data-testid="footer-link-state-overview"
                >
                  {STATE_OVERVIEW_LINK.emoji} {STATE_OVERVIEW_LINK.shortLabel}
                </Link>
              </li>
              {RESEARCH_CLUSTERS.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={c.path}
                    className="text-sm text-slate-600 hover:text-slate-900"
                    data-testid={`footer-link-cluster-${c.slug}`}
                  >
                    {c.emoji} {c.shortLabel}
                  </Link>
                </li>
              ))}
              {FREE_TOOLS.map((t) => (
                <li key={t.slug}>
                  <Link
                    to={t.path}
                    className="text-sm text-slate-600 hover:text-slate-900"
                    data-testid={`footer-link-free-tool-${t.slug}`}
                  >
                    {t.emoji} {t.shortLabel}
                  </Link>
                </li>
              ))}
              {/* Pillar guides — hardcoded entries previously lived here for
                  these three pages (cost index, solar ROI, renovate-or-move).
                  Now driven by the central PILLAR_GUIDES registry so the same
                  list also feeds the homepage "Featured research" rail and
                  any future header dropdown — single source of truth. */}
              {PILLAR_GUIDES.map((g) => (
                <li key={g.slug}>
                  <Link
                    to={g.path}
                    className="text-sm text-slate-600 hover:text-slate-900"
                    data-testid={`footer-link-pillar-${g.slug}`}
                  >
                    {g.emoji} {g.shortLabel}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/pre-bid-scope-checklist"
                  className="text-sm text-slate-600 hover:text-slate-900"
                  data-testid="footer-link-pre-bid-scope"
                >
                  📝 Pre-bid scope checklist
                </Link>
              </li>
              <li>
                <Link
                  to="/how-much-to-spend-fixing-up-before-listing-calculator"
                  className="text-sm text-slate-600 hover:text-slate-900"
                  data-testid="footer-link-pre-listing-spend"
                >
                  🏡 How much to spend before listing?
                </Link>
              </li>
              <li>
                <Link
                  to="/decision-tools"
                  className="text-sm text-slate-600 hover:text-slate-900"
                  data-testid="footer-link-decision-tools"
                >
                  🧭 All 14 decision calculators
                </Link>
              </li>
              <li>
                <Link
                  to="/tools"
                  className="text-sm font-semibold text-emerald-700 hover:text-emerald-800"
                  data-testid="footer-link-tools-index"
                >
                  All free tools →
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-sm font-semibold text-slate-900">Company</h4>
            <ul className="mt-3 space-y-2">
              <li>
                <Link to="/guides" className="text-sm text-slate-600 hover:text-slate-900" data-testid="footer-link-guides">
                  Guides
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-sm text-slate-600 hover:text-slate-900" data-testid="footer-link-about">
                  About
                </Link>
              </li>
              <li>
                <Link to="/methodology" className="text-sm text-slate-600 hover:text-slate-900" data-testid="footer-link-methodology">
                  Methodology
                </Link>
              </li>
              <li>
                <Link to="/editorial-standards" className="text-sm text-slate-600 hover:text-slate-900" data-testid="footer-link-editorial-standards">
                  Editorial Standards
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-slate-600 hover:text-slate-900" data-testid="footer-link-contact">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/sitemap" className="text-sm text-slate-600 hover:text-slate-900" data-testid="footer-link-sitemap">
                  Sitemap
                </Link>
              </li>
              <li>
                <Link to="/press" className="text-sm text-slate-600 hover:text-slate-900" data-testid="footer-link-press">
                  Press &amp; Data
                </Link>
              </li>
              <li>
                <Link to="/license" className="text-sm text-slate-600 hover:text-slate-900" data-testid="footer-link-license">
                  Republish (CC-BY)
                </Link>
              </li>
              <li>
                <Link to="/featured-in" className="text-sm text-slate-600 hover:text-slate-900" data-testid="footer-link-featured-in">
                  Featured In
                </Link>
              </li>
              <li>
                <Link to="/metro-pricing" className="text-sm text-slate-600 hover:text-slate-900" data-testid="footer-link-metro-pricing">
                  Metro Pricing
                </Link>
              </li>
              <li>
                <Link to="/best-of-2026" className="text-sm text-slate-600 hover:text-slate-900" data-testid="footer-link-best-of">
                  Best of 2026
                </Link>
              </li>
              <li>
                <Link to="/whats-new" className="text-sm text-slate-600 hover:text-slate-900" data-testid="footer-link-whats-new">
                  What&apos;s new (Q2 2026)
                </Link>
              </li>
              <li>
                {/* Contractor recruitment funnel — small, neutral placement so
                    homeowners don't perceive the site as contractor-marketing,
                    but contractors actively looking for lead networks can find it. */}
                <Link to="/for-contractors" className="text-sm text-slate-600 hover:text-slate-900" data-testid="footer-link-for-contractors">
                  For contractors
                </Link>
              </li>
              <li>
                {/* Embed gallery — primary entry point for the
                    free-embeddable-calculator program. Lives in the same
                    column as "For contractors" because that's the audience
                    most likely to install. */}
                <Link to="/embeds" className="text-sm text-slate-600 hover:text-slate-900" data-testid="footer-link-embeds">
                  Free embeddable calculators
                </Link>
              </li>
              <li>
                {/* Sponsored / affiliate placement — small, footer-only so it
                    doesn't compete with the editorial nav. Always tagged
                    rel="sponsored" per Google's affiliate-link guidance. */}
                <a
                  href={ANGI_HOMEPAGE_LINK}
                  target="_blank"
                  rel="sponsored nofollow noopener noreferrer"
                  onClick={() => track("angi_affiliate_click", { source: "footer" })}
                  className="text-sm text-slate-600 hover:text-slate-900"
                  data-testid="footer-link-angi"
                >
                  Browse pros on Angi
                  <span className="ml-1 text-[10px] uppercase tracking-wider text-slate-400">Sponsored</span>
                </a>
              </li>
              <li>
                <Link to="/privacy" className="text-sm text-slate-600 hover:text-slate-900" data-testid="footer-link-privacy">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-sm text-slate-600 hover:text-slate-900" data-testid="footer-link-terms">
                  Terms of Service
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-sm font-semibold text-slate-900">Cost Drivers by State</h4>
            <ul className="mt-3 space-y-2">
              {[
                ["hawaii", "Hawaii", "+55%"],
                ["california", "California", "+40%"],
                ["new-york", "New York", "+40%"],
                ["massachusetts", "Massachusetts", "+32%"],
                ["connecticut", "Connecticut", "+30%"],
                ["new-jersey", "New Jersey", "+28%"],
                ["washington", "Washington", "+18%"],
                ["texas", "Texas", "≈ avg"],
                ["florida", "Florida", "≈ avg"],
                ["mississippi", "Mississippi", "−16%"],
              ].map(([slug, name, delta]) => (
                <li key={slug}>
                  <Link
                    to={`/why-is-${slug}-renovation-expensive`}
                    className="flex items-baseline justify-between gap-2 text-sm text-slate-600 hover:text-slate-900"
                    data-testid={`footer-cost-driver-${slug}`}
                  >
                    <span>Why {name}?</span>
                    <span className="shrink-0 text-xs font-medium tabular-nums text-slate-400">
                      {delta}
                    </span>
                  </Link>
                </li>
              ))}
              <li className="pt-1">
                <Link
                  to="/why-renovation-is-expensive"
                  className="text-sm font-semibold text-green-700 hover:text-green-800"
                  data-testid="footer-cost-driver-hub"
                >
                  View all 50 states →
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 border-t border-slate-200 pt-6">
          <p className="text-xs leading-relaxed text-slate-500" data-testid="footer-disclaimer">
            <strong className="text-slate-700">Disclaimer:</strong> Estimates provided by
            HavenCostGuide are for budgeting purposes only and reflect typical national and
            regional ranges. Actual project costs vary based on contractor pricing,
            material availability, structural conditions, and local permit requirements.
            Always obtain at least three written bids from licensed local contractors
            before committing to a project.
          </p>
          <p className="mt-4 text-xs text-slate-400">
            © {new Date().getFullYear()} HavenCostGuide. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
